import { useEffect, useState } from "react";
import { cached, remember } from "@/lib/cache";
import api from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarRange, SlidersHorizontal, Plus, X } from "lucide-react";
import { inr } from "@/lib/fmt";
import { useAuth } from "@/context/AuthContext";
import CustomMetricDialog from "@/components/crm/CustomMetricDialog";

const ALL = [
  ["new_leads", "New leads"], ["qualified", "Qualified"], ["unmarked", "Not yet marked"], ["disqualified", "Disqualified"], ["form_submissions", "Form submissions"],
  ["meetings_booked", "Meets booked"], ["meetings_held", "Meets held"], ["no_shows", "No-shows"], ["show_up_rate_pct", "Show-up rate", "%"],
  ["task_completion_pct", "Task completion", "%"], ["open_tasks", "Open tasks"], ["due_today", "Tasks due today"], ["overdue_tasks", "Overdue tasks"], ["won", "Won"],
  ["won_gmv", "Won GMV", "inr"], ["pipeline_gmv", "Open pipeline GMV", "inr"], ["open_leads", "Open leads"], ["leads_total", "All leads"],
];
const DEFAULT_KEYS = ["new_leads", "qualified", "unmarked", "meetings_booked", "show_up_rate_pct", "task_completion_pct", "due_today", "overdue_tasks"];
const SUB = {
  qualified: (m) => m.new_leads ? `${m.qualified_pct}% of ${m.new_leads}` : null,
  show_up_rate_pct: (m) => (m.meetings_held + m.no_shows) ? `${m.meetings_held}/${m.meetings_held + m.no_shows}` : "no meets yet",
  task_completion_pct: (m) => m.tasks_due ? `${m.tasks_done}/${m.tasks_due}` : null,
};

const day0 = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addD = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export function rangeFor(key, custom) {
  const t = day0(new Date());
  const mon = addD(t, -((t.getDay() + 6) % 7));
  const m1 = new Date(t.getFullYear(), t.getMonth(), 1);
  switch (key) {
    case "today": return [t, addD(t, 1)];
    case "yesterday": return [addD(t, -1), t];
    case "this_week": return [mon, addD(t, 1)];
    case "last_week": return [addD(mon, -7), mon];
    case "this_month": return [m1, addD(t, 1)];
    case "last_month": return [new Date(t.getFullYear(), t.getMonth() - 1, 1), m1];
    case "last_7": return [addD(t, -6), addD(t, 1)];
    case "custom": return [day0(custom.from || t), addD(day0(custom.to || t), 1)];
    default: return [addD(t, -29), addD(t, 1)];
  }
}
const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This week"], ["last_week", "Last week"], ["last_7", "Last 7 days"], ["last_30", "Last 30 days"], ["this_month", "This month"], ["last_month", "Last month"], ["custom", "Custom"]];

export default function MetricsBar({ userEmail, refreshKey }) {
  const storeKey = `crm_metrics_${userEmail}`;
  const saved = JSON.parse(localStorage.getItem(storeKey) || "{}");
  const [preset, setPreset] = useState(saved.preset || "last_30");
  const [custom, setCustom] = useState(saved.custom || {});
  const { user } = useAuth();
  const [keys, setKeys] = useState(() => {
    let k = saved.keys || DEFAULT_KEYS;
    if (!saved.v2 && !k.includes("unmarked")) k = [...k.slice(0, 2), "unmarked", ...k.slice(2)];
    if (!saved.v3 && !k.includes("due_today")) k = [...k.filter((x) => x !== "overdue_tasks"), "due_today", ...(k.includes("overdue_tasks") ? ["overdue_tasks"] : [])];
    return k;
  });
  const [cms, setCms] = useState([]);
  const [newCm, setNewCm] = useState(false);
  const loadCms = () => api.get("/crm/config").then((r) => setCms(r.data.custom_metrics || []));
  useEffect(() => { loadCms(); }, []);
  const [tick, setTick] = useState(0);
  const all = [...ALL, ...cms.map((c) => [`cm_${c.id}`, c.label])];
  const delCm = async (id) => {
    try { await api.put("/crm/config", { custom_metrics: cms.filter((c) => c.id !== id) }); setKeys((k) => k.filter((x) => x !== `cm_${id}`)); loadCms(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete"); }
  };
  const [m, setM] = useState(() => cached(`metrics:${storeKey}`) || null);
  useEffect(() => { localStorage.setItem(storeKey, JSON.stringify({ preset, custom, keys, v2: 1, v3: 1 })); }, [preset, custom, keys, storeKey]);
  useEffect(() => {
    const [s, e] = rangeFor(preset, custom);
    api.get("/crm/metrics", { params: { start: s.toISOString(), end: e.toISOString() } }).then((r) => setM(remember(`metrics:${storeKey}`, r.data)));
  }, [preset, custom, refreshKey, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const fmt = (v, kind) => (v === null || v === undefined ? "—" : kind === "%" ? `${v}%` : kind === "inr" ? inr(v) : v);
  const label = PRESETS.find((p) => p[0] === preset)?.[1];

  return (
    <div className="flex items-center gap-x-5 gap-y-2 min-w-0" data-testid="crm-metrics">
      <div className="flex items-center gap-1 shrink-0">
        <Popover>
          <PopoverTrigger asChild><button className="btn text-xs" data-testid="metrics-range-btn"><CalendarRange size={13} /><span className="hidden md:inline">{label}</span></button></PopoverTrigger>
          <PopoverContent className="w-64 bg-[var(--panel)] border-[var(--border-strong)] p-2" data-testid="metrics-range-pop">
            <div className="grid grid-cols-2 gap-1">
              {PRESETS.map(([k, l]) => (
                <button key={k} onClick={() => setPreset(k)} data-testid={`range-${k}`}
                  className={`px-2 py-1.5 text-xs rounded text-left ${preset === k ? "bg-white text-black" : "hover:bg-[var(--panel-2)]"}`}>{l}</button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="grid grid-cols-2 gap-1 mt-2">
                <input type="date" className="px-2 py-1 rounded border text-xs" value={custom.from || ""} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="range-from" />
                <input type="date" className="px-2 py-1 rounded border text-xs" value={custom.to || ""} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="range-to" />
              </div>
            )}
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild><button className="btn-ghost btn text-xs px-1.5" title="Pick metrics" data-testid="metrics-pick-btn"><SlidersHorizontal size={13} /></button></PopoverTrigger>
          <PopoverContent className="w-60 bg-[var(--panel)] border-[var(--border-strong)] p-2 max-h-80 overflow-auto">
            {all.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-xs py-1 cursor-pointer group">
                <input type="checkbox" checked={keys.includes(k)} data-testid={`metric-toggle-${k}`}
                  onChange={() => setKeys(keys.includes(k) ? keys.filter((x) => x !== k) : [...keys, k])} /><span className="flex-1">{l}</span>
                {k.startsWith("cm_") && user?.role === "admin" && <button className="opacity-0 group-hover:opacity-100 hover:text-[var(--breach)]" onClick={(e) => { e.preventDefault(); delCm(k.slice(3)); }} data-testid={`metric-delete-${k}`}><X size={11} /></button>}
              </label>
            ))}
            {user?.role === "admin" && <button className="btn text-xs w-full justify-center mt-2" onClick={() => setNewCm(true)} data-testid="metric-add-custom"><Plus size={12} />New metric</button>}
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex items-center gap-x-5 overflow-x-auto no-scrollbar min-w-0" data-testid="crm-metrics-strip">
        {all.filter(([k]) => keys.includes(k)).map(([k, l, kind]) => {
          const sub = m && SUB[k]?.(m);
          return (
            <div key={k} className="leading-tight shrink-0">
              <div className="flex items-baseline gap-1.5">
                <span className={`text-lg font-semibold tabular-nums ${k === "overdue_tasks" && m?.[k] ? "text-[var(--breach)]" : k === "due_today" && m?.[k] ? "text-[var(--warn)]" : ""}`} data-testid={`metric-${k}`}>{m ? fmt(m[k], kind) : "…"}</span>
                {sub && <span className="text-[11px] text-[var(--text-faint)] tabular-nums whitespace-nowrap" data-testid={`metric-sub-${k}`}>{sub}</span>}
              </div>
              <div className="text-[10.5px] uppercase tracking-wider text-[var(--text-dim)] whitespace-nowrap">{l}</div>
            </div>
          );
        })}
      </div>
      {newCm && <CustomMetricDialog metrics={cms} onClose={() => setNewCm(false)} onSaved={(k) => { setNewCm(false); loadCms(); setKeys((x) => [...x, k]); setTick((t) => t + 1); }} />}
    </div>
  );
}
